import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    Video, Play, Plus, Film, Eye, Sparkles, CheckCircle2, 
    Clock, AlertOctagon, ExternalLink, RefreshCw, Smartphone
} from 'lucide-react';
import { getProducts, getUserId } from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function SellerVideos() {
    const rtl = isRTL();
    const [videoProducts, setVideoProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activePreviewVideo, setActivePreviewVideo] = useState(null);

    const loadVideoProducts = async () => {
        try {
            setLoading(true);
            const userId = getUserId() || localStorage.getItem('userId');
            // Fetch products with hasVideo = true or filter locally
            const res = await getProducts(0, 100, '', '', '', userId);
            if (res && res.data) {
                const list = res.data.content || (Array.isArray(res.data) ? res.data : []);
                const withVideos = list.filter(p => p.videoUrl || p.mediaUrl?.includes('.mp4') || p.mediaUrl?.includes('.m3u8'));
                setVideoProducts(withVideos);
            }
        } catch (err) {
            console.error('Failed to load seller videos:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadVideoProducts();
    }, []);

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: 22, fontWeight: 800, color: '#f8fafc' }}>
                        {rtl ? 'مقاطع الفيديو الترويجية (Reels)' : 'Product Video Reels & Commerce'}
                    </h1>
                    <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>
                        {rtl 
                            ? 'إدارة مقاطع الفيديو التفاعلية القصيرة HLS التي تظهر في خلاصة الفيديوهات للمتسوقين.' 
                            : 'Manage your high-conversion HLS short videos streaming in the customer video feed.'}
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                    <button 
                        onClick={loadVideoProducts}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#ffffff', border: '1px solid var(--seller-border, #e5e7eb)', color: 'var(--seller-text, #111827)', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                    >
                        <RefreshCw size={15} />
                        <span>{rtl ? 'تحديث' : 'Refresh'}</span>
                    </button>

                    <Link 
                        to="/seller/products/new"
                        style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FE2C55', color: '#ffffff', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 4px 14px rgba(254,44,85,0.35)' }}
                    >
                        <Plus size={16} />
                        <span>{rtl ? 'نشر فيديو منتج جديد' : 'Publish New Video Product'}</span>
                    </Link>
                </div>
            </div>

            {/* Video Analytics Banner */}
            <div style={{ background: 'linear-gradient(135deg, rgba(254,44,85,0.08) 0%, rgba(16,185,129,0.06) 100%)', border: '1px solid rgba(254,44,85,0.2)', borderRadius: 14, padding: '20px 24px', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(254,44,85,0.1)', color: '#FE2C55', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Smartphone size={26} />
                    </div>
                    <div>
                        <h3 style={{ margin: '0 0 4px 0', fontSize: 15, fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                            {rtl ? 'تجربة تسوق بالفيديو الغامر (TikTok / Reels Commerce)' : 'Immersive Video Commerce Engine'}
                        </h3>
                        <p style={{ margin: 0, fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>
                            {rtl 
                                ? 'الفيديوهات المقصوصة بين 5 إلى 35 ثانية تزيد معدل الشراء بنسبة تفوق 300% مقارنة بالصور الثابتة.' 
                                : 'Vertical short clips between 5–35s boost conversion rates by over 300% on mobile storefronts.'}
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 20 }}>
                    <div style={{ textAlign: 'center', padding: '0 12px' }}>
                        <span style={{ fontSize: 20, fontWeight: 800, color: '#FE2C55' }}>{videoProducts.length}</span>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--seller-muted, #64748b)' }}>{rtl ? 'مقاطع نشطة' : 'Active Clips'}</span>
                    </div>
                    <div style={{ textAlign: 'center', padding: '0 12px', borderLeft: '1px solid var(--seller-border, #e5e7eb)' }}>
                        <span style={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>1080p HLS</span>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--seller-muted, #64748b)' }}>{rtl ? 'جودة البث' : 'Streaming Quality'}</span>
                    </div>
                </div>
            </div>

            {/* Videos Grid */}
            {loading ? (
                <div style={{ padding: '60px 0', textAlign: 'center' }}>
                    <Spinner />
                </div>
            ) : videoProducts.length === 0 ? (
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '60px 20px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <Video size={48} color="#64748b" style={{ opacity: 0.4, marginBottom: 14 }} />
                    <h3 style={{ margin: '0 0 8px 0', fontSize: 16, color: 'var(--seller-text, #111827)' }}>
                        {rtl ? 'لا توجد مقاطع فيديو مضافة بعد' : 'No Video Reels Uploaded Yet'}
                    </h3>
                    <p style={{ margin: '0 0 20px 0', fontSize: 13, color: 'var(--seller-muted, #64748b)', maxWidth: 460, marginInline: 'auto' }}>
                        {rtl 
                            ? 'أضف مقاطع فيديو قصيرة لمنتجاتك لجذب المشترين في خلاصة الفيديوهات الفاخرة.' 
                            : 'Upload short engaging videos for your luxury products to captivate buyers in the global video feed.'}
                    </p>
                    <Link 
                        to="/seller/products/new"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FE2C55', color: '#ffffff', padding: '10px 22px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 4px 14px rgba(254,44,85,0.35)' }}
                    >
                        <Plus size={16} />
                        <span>{rtl ? 'نشر أول فيديو لمنتجك' : 'Publish Your First Video'}</span>
                    </Link>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
                    {videoProducts.map(prod => {
                        const img = prod.primaryImageUrl || prod.imageUrl || (prod.images && prod.images[0]?.url) || '';
                        const isApproved = prod.approvalStatus === 'APPROVED' || prod.active;

                        return (
                            <div 
                                key={prod.id}
                                style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                            >
                                {/* Video Thumbnail / Player Frame */}
                                <div style={{ position: 'relative', width: '100%', aspectRatio: '9/16', maxHeight: 320, background: '#111827', overflow: 'hidden' }}>
                                    {img ? (
                                        <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Film size={40} color="#64748b" />
                                        </div>
                                    )}

                                    {/* Play Overlay Button */}
                                    <button 
                                        onClick={() => setActivePreviewVideo(prod.videoUrl || prod.mediaUrl)}
                                        style={{ position: 'absolute', inset: 0, margin: 'auto', width: 50, height: 50, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', border: '2px solid rgba(254,44,85,0.85)', color: '#FE2C55', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease' }}
                                    >
                                        <Play size={22} style={{ marginLeft: 2 }} />
                                    </button>

                                    {/* Status Badge */}
                                    <div style={{ position: 'absolute', top: 12, right: 12 }}>
                                        {isApproved ? (
                                            <span style={{ background: 'rgba(16,185,129,0.9)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, backdropFilter: 'blur(4px)' }}>
                                                LIVE REEL
                                            </span>
                                        ) : (
                                            <span style={{ background: 'rgba(245,158,11,0.9)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, backdropFilter: 'blur(4px)' }}>
                                                PENDING
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Video Info */}
                                <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <div>
                                        <h4 style={{ margin: '0 0 6px 0', fontSize: 14, fontWeight: 700, color: 'var(--seller-text, #111827)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {prod.nameAr || prod.name}
                                        </h4>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>
                                            <span>{prod.categoryName || 'Luxury'}</span>
                                            <span style={{ color: '#FE2C55', fontWeight: 700 }}>{prod.price} {prod.currency || 'SAR'}</span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--seller-border, #e5e7eb)' }}>
                                        <Link 
                                            to={`/seller/products/new?edit=${prod.id}`}
                                            style={{ flex: 1, textAlign: 'center', background: '#f3f4f6', color: 'var(--seller-text, #111827)', border: '1px solid var(--seller-border, #e5e7eb)', padding: '7px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
                                        >
                                            {rtl ? 'تعديل الفيديو' : 'Edit Video'}
                                        </Link>
                                        <Link 
                                            to={`/videos`}
                                            target="_blank"
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(254,44,85,0.1)', border: '1px solid rgba(254,44,85,0.25)', color: '#FE2C55', padding: '7px 10px', borderRadius: 6, fontSize: 12, textDecoration: 'none' }}
                                            title="View in customer video feed"
                                        >
                                            <ExternalLink size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Video Playback Modal */}
            {activePreviewVideo && (
                <div 
                    onClick={() => setActivePreviewVideo(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                >
                    <div 
                        onClick={e => e.stopPropagation()}
                        style={{ position: 'relative', width: '100%', maxWidth: 400, aspectRatio: '9/16', background: '#000', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.8)' }}
                    >
                        <video 
                            src={activePreviewVideo}
                            controls
                            autoPlay
                            playsInline
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                        <button 
                            onClick={() => setActivePreviewVideo(null)}
                            style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
